/**
 * FollowButton - Shows follow status and allows follow/unfollow.
 * States: Follow, Following (click to unfollow), Pending Request.
 */

import React, { useState, useEffect } from 'react';
import { followUser, unfollowUser, getFollowStatus } from '../../helpers/usersHelpers';
import toast from 'react-hot-toast';

function FollowButton({ targetUserId, initialStatus, onStatusChange, compact }) {
  const [status, setStatus] = useState(initialStatus ?? null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (targetUserId && initialStatus === undefined && status === null) {
      getFollowStatus(targetUserId).then(setStatus);
    }
  }, [targetUserId, initialStatus, status]);

  const effectiveStatus = status ?? initialStatus;

  const handleClick = async () => {
    if (!targetUserId || loading) return;
    setLoading(true);
    try {
      if (effectiveStatus === 'accepted') {
        await unfollowUser(targetUserId);
        setStatus(null);
        onStatusChange?.(null);
        toast.success('Unfollowed');
      } else if (effectiveStatus === 'pending') {
        await unfollowUser(targetUserId);
        setStatus(null);
        onStatusChange?.(null);
        toast.success('Request cancelled');
      } else {
        const result = await followUser(targetUserId);
        const newStatus = result?.status ?? 'accepted';
        setStatus(newStatus);
        onStatusChange?.(newStatus);
        toast.success(newStatus === 'pending' ? 'Request sent' : 'Following');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialStatus !== undefined) {
      setStatus(initialStatus);
    }
  }, [initialStatus]);

  const buttonClass = compact
    ? 'px-3 py-1 rounded-lg text-sm font-medium'
    : 'py-2 px-4 rounded-xl text-sm font-medium';

  if (effectiveStatus === 'accepted') {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        className={`${buttonClass} bg-shadow text-ash border border-shadow hover:border-ether hover:text-ether`}
      >
        {loading ? '...' : 'Following'}
      </button>
    );
  }

  if (effectiveStatus === 'pending') {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        className={`${buttonClass} bg-shadow text-silver border border-shadow`}
      >
        {loading ? '...' : 'Pending Request'}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className={`${buttonClass} bg-ether text-void`}
    >
      {loading ? '...' : 'Follow'}
    </button>
  );
}

export default FollowButton;
